<!-- /components/app/cases/dashboard-widgets/ActivityWidget.vue -->
<script setup lang="ts">
  import { ref, onMounted } from 'vue'
  import {
    Bell,
    ChevronRightIcon,
    CheckCircle,
    Mail,
    Calendar,
    FileText,
    File
  } from 'lucide-vue-next'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import { ActivityTypeEnum, type Activity } from '@/types/generated'
  import { useToast } from '@/components/ui/toast'
  import { useRouter } from 'vue-router'

  const router = useRouter()
  const store = useCaseStore()
  const { toast } = useToast()
  const isLoading = ref(false)
  const displayedActivities = ref<Activity[]>([])

  // Expose action button for the widget (like in Documents widget)
  const actionLabel = 'Aktivitäten'
  const actionIcon = Bell
  const handleAction = () => {
    if (store.caseRecord?.uuid) {
      router.push(`/app/cases/${store.caseRecord.uuid}/activities`)
    }
  }
  defineExpose({
    actionLabel,
    actionIcon,
    handleAction
  })

  onMounted(async () => {
    await loadActivities()
  })

  const loadActivities = async () => {
    try {
      isLoading.value = true

      // Get all activities from the case record
      const allActivities = [...(store.caseRecord?.activities || [])]

      // Process and sort activities by date (newest first)
      const processedActivities = allActivities.sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )

      // Get only the first few activities for display
      displayedActivities.value = processedActivities.slice(0, 5)
    } catch (error) {
      console.error('Error loading activities:', error)
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: 'Aktivitäten konnten nicht geladen werden'
      })
    } finally {
      isLoading.value = false
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // Get activity icon based on activity type
  const getActivityIcon = (activityType: ActivityTypeEnum) => {
    switch (activityType) {
      case ActivityTypeEnum.DOCUMENT_SIGNED:
        return File
      case ActivityTypeEnum.QUESTIONNAIRE_CLOSED:
        return CheckCircle
      case ActivityTypeEnum.EMAIL_RECEIVED:
      case ActivityTypeEnum.EMAIL_SENT:
        return Mail
      case ActivityTypeEnum.APPOINTMENT_CREATED:
        return Calendar
      case ActivityTypeEnum.CASE_CREATED:
        return FileText
      default:
        return Bell
    }
  }

  // Get color for activity icon/badge
  const getActivityColor = (activityType: ActivityTypeEnum) => {
    switch (activityType) {
      case ActivityTypeEnum.DOCUMENT_SIGNED:
        return {
          bg: 'bg-blue-500',
          text: 'text-white',
          border: 'border-blue-500'
        }
      case ActivityTypeEnum.QUESTIONNAIRE_CLOSED:
        return {
          bg: 'bg-green-500',
          text: 'text-white',
          border: 'border-green-500'
        }
      case ActivityTypeEnum.EMAIL_RECEIVED:
        return {
          bg: 'bg-purple-500',
          text: 'text-white',
          border: 'border-purple-500'
        }
      case ActivityTypeEnum.EMAIL_SENT:
        return {
          bg: 'bg-gray-500',
          text: 'text-white',
          border: 'border-gray-500'
        }
      case ActivityTypeEnum.APPOINTMENT_CREATED:
        return {
          bg: 'bg-amber-500',
          text: 'text-white',
          border: 'border-amber-500'
        }
      case ActivityTypeEnum.CASE_CREATED:
        return {
          bg: 'bg-indigo-500',
          text: 'text-white',
          border: 'border-indigo-500'
        }
      default:
        return {
          bg: 'bg-gray-500',
          text: 'text-white',
          border: 'border-gray-500'
        }
    }
  }

  // Get activity label based on activity type
  const getActivityLabel = (activityType: ActivityTypeEnum) => {
    switch (activityType) {
      case ActivityTypeEnum.DOCUMENT_SIGNED:
        return 'Dokument unterschrieben'
      case ActivityTypeEnum.QUESTIONNAIRE_CLOSED:
        return 'Fragebogen abgeschlossen'
      case ActivityTypeEnum.EMAIL_RECEIVED:
        return 'E-Mail empfangen'
      case ActivityTypeEnum.EMAIL_SENT:
        return 'E-Mail gesendet'
      case ActivityTypeEnum.APPOINTMENT_CREATED:
        return 'Termin vereinbart'
      case ActivityTypeEnum.CASE_CREATED:
        return 'Akte erstellt'
      default:
        return activityType
    }
  }

  // Check if activity has been seen
  const isActivitySeen = (activity: Activity) => {
    return activity.seen_at !== null
  }

  // Navigate to full activities view
  const viewAllActivities = () => {
    if (store.caseRecord?.uuid) {
      router.push(`/cases/activity/`)
    }
  }
</script>

<template>
  <div class="activity-widget">
    <!-- Loading State -->
    <div
      v-if="isLoading"
      class="flex justify-center py-6"
    >
      <div
        class="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600"
      ></div>
    </div>

    <!-- No Activities State -->
    <div
      v-else-if="displayedActivities.length === 0"
      class="flex flex-col items-center justify-center rounded-lg border border-gray-300 px-4 py-10 shadow-sm"
    >
      <Bell class="h-10 w-10 text-gray-400" />
      <p class="text-md mt-5 text-center text-gray-500">Keine Aktivitäten vorhanden</p>
    </div>

    <!-- Timeline View -->
    <div
      v-else
      class="relative ml-7 pt-2"
    >
      <!-- Vertical Timeline Line -->
      <div class="absolute bottom-0 left-3.5 top-0 w-px bg-gray-200"></div>

      <!-- Activity Items -->
      <div
        v-for="activity in displayedActivities"
        :key="activity.uuid"
        class="relative mb-6 last:mb-0"
      >
        <!-- Timeline Circle -->
        <div
          class="absolute left-[-7px] z-10 flex h-7 w-7 items-center justify-center rounded-full border-2
            border-white"
          :class="getActivityColor(activity.activity_type).bg"
        >
          <component
            :is="getActivityIcon(activity.activity_type)"
            class="h-3.5 w-3.5 text-white"
          />
        </div>

        <!-- Activity Card -->
        <div
          class="ml-6 rounded-lg border border-gray-200 p-4 shadow-sm"
          :class="{ 'border-purple-100 bg-purple-50': !isActivitySeen(activity) }"
        >
          <!-- Activity Header -->
          <div class="mb-2 flex justify-between">
            <h4 class="font-medium text-gray-900">
              {{ getActivityLabel(activity.activity_type) }}
              <span
                v-if="!isActivitySeen(activity)"
                class="ml-2 rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-800"
              >
                Neu
              </span>
            </h4>
            <time class="text-sm text-gray-500">
              {{ formatDate(activity.created_at) }} um {{ formatTime(activity.created_at) }} Uhr
            </time>
          </div>

          <!-- Activity Details -->
          <div class="text-sm text-gray-600">
            <template v-if="activity.activity_type === ActivityTypeEnum.DOCUMENT_SIGNED">
              Ein Dokument wurde unterschrieben und ist im System eingegangen.
            </template>
            <template v-else-if="activity.activity_type === ActivityTypeEnum.QUESTIONNAIRE_CLOSED">
              Ein Fragebogen wurde ausgefüllt und abgeschlossen.
            </template>
            <template v-else-if="activity.activity_type === ActivityTypeEnum.EMAIL_RECEIVED">
              Eine neue E-Mail ist eingegangen.
            </template>
            <template v-else-if="activity.activity_type === ActivityTypeEnum.EMAIL_SENT">
              Eine E-Mail wurde gesendet.
            </template>
            <template v-else-if="activity.activity_type === ActivityTypeEnum.APPOINTMENT_CREATED">
              Ein neuer Termin wurde vereinbart.
            </template>
            <template v-else-if="activity.activity_type === ActivityTypeEnum.CASE_CREATED">
              Eine neue Akte wurde erstellt.
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- View All Link -->
    <!-- <div class="items-left mt-4 flex justify-end">
      <a
        href="#"
        class="inline-flex underline items-center text-sm font-medium text-purple-600 hover:text-purple-800"
        @click.prevent="viewAllActivities"
      >
        Alle Aktivitäten ansehen
        <ChevronRightIcon class="ml-1 h-4 w-4" />
      </a>
    </div> -->
  </div>
</template>

<style scoped>
  .activity-widget {
    width: 100%;
    box-sizing: border-box;
  }
</style>
